// QcSamplingSignatureSection.js
// This component handles the QA/QC Sampling Signature section of the Wells tab

import React, { useState } from "react";
import Select from "react-select";
// Components
import { ConfirmationModal } from "../../../common/ConfirmationModal";
import { SamplingEventDatePicker } from "../../../common/SamplingEventDatePicker";
import { SamplingEventTimePickerInput } from "../../../common/SamplingEventTimePickerInput";
import { SignatureModal } from "../../../../common/SignatureModal";
import styles from "./QcSamplingSignatureSection.module.css";
import commonStyles from "../WellsTab.module.css";

// Employee select component for the SignatureModal
const QcEmployeeSelect = ({
  well,
  onEmployeeChange,
  qaQcEmployees,
  samplingEventMode,
}) => {
  const handleEmployeeChange = (selected) => {
    if (!onEmployeeChange) {
      return;
    }

    if (!selected) {
      onEmployeeChange(null, null);
    } else {
      onEmployeeChange(selected.value, selected.label);
    }
  };

  return (
    <Select
      value={
        well.qcSamplingSignatureEmployeeId
          ? {
              value: well.qcSamplingSignatureEmployeeId,
              label: well.qcSamplingSignatureEmployeeName,
            }
          : null
      }
      onChange={handleEmployeeChange}
      options={qaQcEmployees.map((emp) => ({
        value: emp.employee_id,
        label: emp.name,
      }))}
      isDisabled={samplingEventMode === "view"}
      placeholder="Select QA/QC Reviewer"
      isClearable={true}
      isSearchable={true}
      className={styles.employeeSelect}
    />
  );
};

// Main QcSamplingSignatureSection Component
export const QcSamplingSignatureSection = ({
  well,
  qaQcEmployees,
  samplingEventMode = "edit",
  ui,
  wellsActions,
  emitPatch,
}) => {
  // Computed values from well data
  const hasEmployee = Boolean(well.qcSamplingSignatureEmployeeId);
  const hasSignature = Boolean(well.qcSamplingSignature);
  const isSignatureModalOpen = ui?.isQcSignatureModalOpen || false;
  const isDeleteConfirmOpen = ui?.isQcSignatureConfirmationModalOpen || false;

  // Local state for tracking employee selection during modal editing
  const [pendingEmployeeId, setPendingEmployeeId] = useState(null);
  const [pendingEmployeeName, setPendingEmployeeName] = useState(null);

  // Revision counter to force re-renders when updating local pending state
  const [, setRevision] = useState(0);

  // Handle well field updates using the same pattern as WellCard.js
  const handleUpdateWell = (field, value) => {
    wellsActions.updateWellSample(well.id, [{ field, value }], emitPatch);
  };

  // Handle employee selection change in the modal
  const handleEmployeeSelectionChange = (employeeId, employeeName) => {
    setPendingEmployeeId(employeeId);
    setPendingEmployeeName(employeeName);
    setRevision((r) => r + 1);
  };

  // Handle opening signature modal
  const handleSignatureClick = () => {
    if (samplingEventMode === "view") return;
    // Initialize pending values with current well values
    setPendingEmployeeId(well.qcSamplingSignatureEmployeeId);
    setPendingEmployeeName(well.qcSamplingSignatureEmployeeName);
    wellsActions.openQcSignatureModal(well.id);
  };

  // Handle saving signature from the modal
  const handleSaveSignature = (signatureData) => {
    // Update all QC signature fields at once
    const updates = [
      { field: "qcSamplingSignatureEmployeeId", value: pendingEmployeeId },
      { field: "qcSamplingSignatureEmployeeName", value: pendingEmployeeName },
      { field: "qcSamplingSignature", value: signatureData },
    ];

    wellsActions.updateWellSample(well.id, updates, emitPatch);
    wellsActions.closeQcSignatureModal();

    // Clear pending state
    setPendingEmployeeId(null);
    setPendingEmployeeName(null);
  };

  // Handle closing the signature modal
  const handleCloseSignatureModal = () => {
    wellsActions.closeQcSignatureModal();
    // Clear pending state
    setPendingEmployeeId(null);
    setPendingEmployeeName(null);
  };

  // Handle delete click - open confirmation dialog
  const handleDeleteClick = () => {
    wellsActions.openQcSignatureConfirmationModal();
  };

  // Cancel deletion
  const handleCancelDelete = () => {
    wellsActions.closeQcSignatureConfirmationModal();
  };

  // Confirm deletion - clear signature data but keep date/time
  const handleConfirmDelete = () => {
    const updates = [
      { field: "qcSamplingSignatureEmployeeId", value: null },
      { field: "qcSamplingSignatureEmployeeName", value: null },
      { field: "qcSamplingSignature", value: "" },
    ];
    wellsActions.updateWellSample(well.id, updates, emitPatch);
    wellsActions.closeQcSignatureConfirmationModal();
  };

  // Handle auto-fill button for date and time
  const handleAutoFillDateTime = () => {
    const currentDate = new Date();
    const formattedTime = currentDate.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // Update both fields at once
    const updates = [
      { field: "qcSamplingSignatureDate", value: currentDate },
      { field: "qcSamplingSignatureTime", value: formattedTime },
    ];
    wellsActions.updateWellSample(well.id, updates, emitPatch);
  };

  // Handle date change
  const handleDateChange = (date) => {
    handleUpdateWell("qcSamplingSignatureDate", date);
  };

  // Handle time input change (for onChange - no emit)
  const handleTimeInputChange = (time) => {
    wellsActions.updateWellSample(well.id, [
      { field: "qcSamplingSignatureTime", value: time },
    ]);
  };

  // Handle time blur (for onBlur - emit to server)
  const handleTimeBlur = (e) => {
    handleUpdateWell("qcSamplingSignatureTime", e.target.value);
  };

  return (
    <div className={styles.samplerSignaturesSection}>
      <div className={styles.sectionHeader}>
        <h4>7. QA/QC Sampling Review</h4>
      </div>

      <div className={styles.dateTimeRow}>
        <button
          className={styles.autoFillButton}
          onClick={handleAutoFillDateTime}
          title="Auto-fill current date and time"
          disabled={samplingEventMode === "view"}
        >
          AutoFill Date/Time
        </button>

        <div className={styles.dateTimeGroup}>
          <div className={styles.dateInputWrapper}>
            <label htmlFor={`qcSamplingSignatureDate-${well.id}`}>
              QA/QC Review Date:
            </label>
            <SamplingEventDatePicker
              id={`qcSamplingSignatureDate-${well.id}`}
              selected={well.qcSamplingSignatureDate}
              onChange={handleDateChange}
              dateFormat="MM/dd/yyyy"
              className={styles.dateInput}
              placeholderText="QA/QC Review Date"
              isClearable={true}
              showPopperArrow={false}
              onFocus={(e) => (e.target.readOnly = true)}
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              disabled={samplingEventMode === "view"}
            />
          </div>

          <div className={styles.timeInputWrapper}>
            <label htmlFor={`qcSamplingSignatureTime-${well.id}`}>
              QA/QC Review Time:
            </label>
            <SamplingEventTimePickerInput
              id={`qcSamplingSignatureTime-${well.id}`}
              value={well.qcSamplingSignatureTime}
              onChange={handleTimeInputChange}
              onBlur={handleTimeBlur}
              className={styles.timeInput}
              disabled={samplingEventMode === "view"}
            />
          </div>
        </div>
      </div>

      <div className={styles.signatureCardList}>
        <div className={styles.signatureCard}>
          <div className={styles.signatureCardHeader}>
            <label className={styles.signatureLabel}>QA/QC Reviewer</label>
            <div className={styles.signatureValue}>
              {well.qcSamplingSignatureEmployeeName || (
                <span className={styles.emptyValue}>No reviewer selected</span>
              )}
            </div>
          </div>

          <div
            className={styles.signatureDisplay}
            onClick={
              samplingEventMode !== "view" ? handleSignatureClick : undefined
            }
          >
            {hasSignature ? (
              <img
                src={well.qcSamplingSignature}
                alt="QA/QC signature"
                className={styles.signatureImage}
              />
            ) : (
              <div className={styles.emptySignature}>
                <span>Click to sign</span>
              </div>
            )}
          </div>

          <div className={styles.signatureCardActions}>
            <button
              className={styles.signButton}
              onClick={handleSignatureClick}
              disabled={samplingEventMode === "view"}
            >
              {hasSignature ? "Edit" : "Sign"}
            </button>
            <button
              className={styles.deleteButton}
              onClick={handleDeleteClick}
              disabled={
                samplingEventMode === "view" || (!hasEmployee && !hasSignature)
              }
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Signature Modal with Employee Select */}
      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={handleCloseSignatureModal}
        signee={`QC_${well.id}`}
        existingSignature={well.qcSamplingSignature}
        onSave={handleSaveSignature}
        onClear={() => {}}
        components={[
          <QcEmployeeSelect
            key="qcEmployeeSelect"
            well={{
              qcSamplingSignatureEmployeeId:
                pendingEmployeeId ?? well.qcSamplingSignatureEmployeeId,
              qcSamplingSignatureEmployeeName:
                pendingEmployeeName ?? well.qcSamplingSignatureEmployeeName,
            }}
            onEmployeeChange={handleEmployeeSelectionChange}
            qaQcEmployees={qaQcEmployees || []}
            samplingEventMode={samplingEventMode}
          />,
        ]}
      />

      {/* Confirmation Modal for deletion */}
      {isDeleteConfirmOpen && (
        <ConfirmationModal
          title="Clear QA/QC Signature"
          message="Are you sure you want to clear this signature? This action cannot be undone."
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          styles={{
            confirmationModalOverlay: commonStyles.modalOverlay,
            confirmationModalContent: commonStyles.modalContent,
            confirmationModalHeader: commonStyles.modalHeader,
            confirmationModalMessage: commonStyles.modalMessage,
            confirmationModalButtons: commonStyles.modalButtons,
            confirmCancel: commonStyles.cancelButton,
            confirmButton: commonStyles.confirmButton,
          }}
          confirmButtonClass="confirmButton"
        />
      )}
    </div>
  );
};
